/**
 * fr-cart-drawer.js
 * Fragrance Republic – Custom Cart Drawer Controller
 */

(function () {
  "use strict";

  const CONFIG = window.FRCartConfig || {
    freeShippingThreshold: 4000,
    currency: "GBP",
    moneyFormat: "£{{amount}}",
    cartUrl: "/cart",
    checkoutUrl: "/checkout",
  };

  const drawer = document.getElementById("fr-cart-drawer");
  const overlay = document.getElementById("fr-cart-overlay");
  const closeBtn = document.getElementById("fr-cart-close");
  const body = document.getElementById("fr-cart-body");
  const itemsEl = document.getElementById("fr-cart-items");
  const emptyEl = document.getElementById("fr-cart-empty");
  const contentEl = document.getElementById("fr-cart-content");
  const footerEl = document.getElementById("fr-cart-footer");
  const subtotalEl = document.getElementById("fr-cart-subtotal");
  const countBadge = document.getElementById("fr-cart-count-badge");
  const shippingBar = document.getElementById("fr-shipping-bar");
  const shippingFill = document.getElementById("fr-shipping-fill");
  const shippingText = document.getElementById("fr-shipping-text");
  const discountToggle = document.getElementById("fr-discount-toggle");
  const discountForm = document.getElementById("fr-discount-form");
  const discountInput = document.getElementById("fr-discount-input");
  const discountApply = document.getElementById("fr-discount-apply");
  const discountMsg = document.getElementById("fr-discount-msg");
  const continueShopping = document.getElementById("fr-cart-continue-shopping");
  const continueBrowsing = document.getElementById("fr-cart-continue-browsing");
  const viewBagBtn = document.getElementById("fr-cart-view-bag");

  if (!drawer) return;

  let cartState = null;
  let cartFetchPromise = null;
  let isUpdating = false;
  let isOpen = false;
  let returnFocus = null;
  let previousPageInert = false;
  const pageContainer = document.getElementById("PageContainer");
  const feedbackEl = document.getElementById("fr-cart-feedback");
  function cartFeedback(message, failed) {
    if (!feedbackEl) return;
    feedbackEl.hidden = !message;
    feedbackEl.querySelector('[data-fr-cart-feedback-text]').textContent = message || '';
    feedbackEl.querySelector('[data-fr-cart-feedback-actions]').hidden = !failed;
  }
  drawer.inert = true;

  function formatMoney(cents) {
    const pounds = (cents / 100).toFixed(2);
    return CONFIG.moneyFormat
      .replace("{{amount}}", pounds)
      .replace("{{amount_no_decimals}}", Math.floor(cents / 100));
  }

  let previewFrameObserver;
  function syncPreviewBarSpace() {
    const frame = document.getElementById("PBarNextFrameWrapper") || document.getElementById("PBarNextFrame");
    let overlap = 0;
    if (frame) {
      const rect = frame.getBoundingClientRect();
      if (rect.height > 0 && rect.height < 160 && rect.width > 0 && rect.bottom >= window.innerHeight - 1 && getComputedStyle(frame).visibility !== "hidden") {
        overlap = Math.max(0, window.innerHeight - rect.top);
      }
      if (!previewFrameObserver) {
        previewFrameObserver = new ResizeObserver(syncPreviewBarSpace);
        previewFrameObserver.observe(frame);
      }
    }
    drawer.style.setProperty("--fr-cart-preview-offset", `${overlap}px`);
  }
  const previewInsertionObserver = new MutationObserver(() => {
    if (document.getElementById("PBarNextFrame")) {
      syncPreviewBarSpace();
      previewInsertionObserver.disconnect();
    }
  });
  previewInsertionObserver.observe(document.body, { childList: true, subtree: true });
  window.addEventListener("resize", syncPreviewBarSpace);
  syncPreviewBarSpace();

  function openCartShell() {
    syncPreviewBarSpace();
    if (!isOpen) {
      returnFocus = document.activeElement;
      previousPageInert = pageContainer ? pageContainer.inert : false;
    }
    drawer.inert = false;
    if (pageContainer) pageContainer.inert = true;
    isOpen = true;
    drawer.classList.add("is-open");
    drawer.setAttribute("aria-hidden", "false");
    overlay.classList.add("is-visible");
    overlay.setAttribute("aria-hidden", "false");
    document.body.classList.add("fr-cart-open");
    document.body.style.overflow = "hidden";
    closeBtn.focus();
  }

  function openCart() {
    openCartShell();
    fetchCart();
  }

  function closeCart() {
    isOpen = false;
    drawer.classList.remove("is-open");
    drawer.setAttribute("aria-hidden", "true");
    overlay.classList.remove("is-visible");
    overlay.setAttribute("aria-hidden", "true");
    document.body.classList.remove("fr-cart-open");
    document.body.style.overflow = "";
    if (pageContainer) pageContainer.inert = previousPageInert;
    if (returnFocus && returnFocus.isConnected) returnFocus.focus({preventScroll: true});
    drawer.inert = true;
    pickerReady = false;
    pickerExpanded = false;
    const picker = document.getElementById("fr-bundle-picker");
    if (picker) picker.classList.add("fr-is-hidden");
    if (shippingBar) {
      shippingBar.classList.remove("fr-bundle-offer--expanded");
      if (shippingBar.classList.contains("fr-shipping-bar--clickable"))
        shippingBar.setAttribute("aria-expanded", "false");
    }
  }

  async function fetchCart() {
    if (cartFetchPromise) return cartFetchPromise;

    cartFetchPromise = (async () => {
      try {
        cartFeedback(drawer.dataset.frLoading, false);
        drawer.setAttribute('aria-busy', 'true');
        setCheckoutAvailable(false);
        const res = await fetch(`/cart.js?fr_cart_drawer=${Date.now()}`, {
          headers: {
            "Content-Type": "application/json",
            "X-Requested-With": "XMLHttpRequest",
          },
          cache: "no-store",
          credentials: "include",
        });
        if (!res.ok) throw new Error("Cart fetch failed");
        cartState = await res.json();
        cartFeedback('', false);
        renderCart();
        return cartState;
      } catch (err) {
        console.error("[FR Cart]", err);
        cartFeedback(drawer.dataset.frLoadError, true);
        setCheckoutAvailable(false);
        return null;
      } finally {
        drawer.removeAttribute('aria-busy');
      }
    })();

    try {
      return await cartFetchPromise;
    } finally {
      cartFetchPromise = null;
    }
  }

  function hydrateCart(cart, shouldOpen) {
    if (cart && Array.isArray(cart.items)) {
      cartState = cart;
      renderCart();
    }
    if (shouldOpen) openCartShell();
  }

  function isDiscoveryLine(item) {
    return item.properties?._fr_discovery_offer === "fr-discovery-6x2-v1";
  }
  function isDiscoveryPack(item) {
    return isDiscoveryLine(item) && Number(item.variant_id || item.id) === 60259428270464;
  }
  function isGiftItem(item) {
    const giftFlag = item && item.properties && item.properties._gift;
    return giftFlag === true || String(giftFlag).toLowerCase() === "true";
  }

  function getCustomerItemCount(items, changedKey, changedQty) {
    return (items || []).reduce((count, item) => {
      if (isGiftItem(item) || (isDiscoveryLine(item) && !isDiscoveryPack(item))) return count;
      const quantity = item.key === changedKey ? changedQty : item.quantity;
      return count + Math.max(0, Number(quantity) || 0);
    }, 0);
  }

  function setCheckoutAvailable(isAvailable) {
    const checkoutLink = document.getElementById("fr-cart-checkout");
    const express = document.getElementById("fr-cart-express");
    if (express) express.inert = !isAvailable;
    if (!checkoutLink) return;

    if (!isAvailable) {
      const href = checkoutLink.getAttribute("href");
      if (href) checkoutLink.dataset.frCheckoutHref = href;
      checkoutLink.removeAttribute("href");
      checkoutLink.setAttribute("aria-disabled", "true");
      checkoutLink.setAttribute("tabindex", "-1");
      return;
    }

    checkoutLink.setAttribute(
      "href",
      checkoutLink.dataset.frCheckoutHref || CONFIG.checkoutUrl,
    );
    delete checkoutLink.dataset.frCheckoutHref;
    checkoutLink.removeAttribute("aria-disabled");
    checkoutLink.removeAttribute("tabindex");
  }

  function renderEmptyCartState() {
    updateBadges(0);
    emptyEl.classList.remove("fr-is-hidden");
    contentEl.classList.add("fr-is-hidden");
    footerEl.classList.add("fr-is-hidden");
    footerEl.classList.remove("fr-cart__footer--compact");
    if (itemsEl) itemsEl.innerHTML = "";
    if (shippingText) shippingText.textContent = "";
    if (shippingFill) shippingFill.style.width = "0%";
    pickerExpanded = false;
    const picker = document.getElementById("fr-bundle-picker");
    if (picker) picker.classList.add("fr-is-hidden");
    if (shippingBar) {
      shippingBar.classList.remove("fr-bundle-offer--expanded");
      shippingBar.setAttribute("aria-expanded", "false");
    }
    setCheckoutAvailable(false);
  }

  function renderCart() {
    if (!cartState) return;
    const count = getCustomerItemCount(cartState.items);
    const total = cartState.total_price;
    updateBadges(count);
    if (count === 0) {
      renderEmptyCartState();
      return;
    } else {
      emptyEl.classList.add("fr-is-hidden");
      contentEl.classList.remove("fr-is-hidden");
      footerEl.classList.remove("fr-is-hidden");
      const walletTemplate = document.getElementById("fr-cart-express-template");
      if (walletTemplate) {
        walletTemplate.parentElement.append(walletTemplate.content.cloneNode(true));
        walletTemplate.remove();
      }
      layoutExpressWallets();
      setCheckoutAvailable(true);
    }
    const paidOriginal = cartState.items.filter(item => !isGiftItem(item)).reduce((sum, item) => sum + item.original_line_price, 0);
    const giftFinal = cartState.items.filter(isGiftItem).reduce((sum, item) => sum + item.final_line_price, 0);
    const paidSavings = Math.max(0, paidOriginal - (total - giftFinal));
    renderOrderSummary(total, paidSavings, paidOriginal);
    renderPurchaseGuidance(total);
    renderShippingBar(total);
    renderItems(cartState.items);
  }

  /* ─── CART CLASSIFICATION — excludes auto-gift items ─────── */
  function classifyCart(items) {
    let bottleQty = 0;
    let sampleQty = 0;
    (items || []).forEach((item) => {
      // Exclude auto-added gift items from bundle/sample counts
      if (isGiftItem(item) || isDiscoveryLine(item)) return;
      const variant = (item.variant_title || "").toLowerCase();
      const isSample =
        /(?<!\d)5ml(?!\d)/i.test(variant) ||
        variant.includes("sample") ||
        item.price <= 1499;
      if (isSample) {
        sampleQty += item.quantity;
      } else {
        bottleQty += item.quantity;
      }
    });
    return { bottleQty, sampleQty };
  }

  function layoutExpressWallets() {
    const express = document.getElementById("fr-cart-express");
    if (!express) return;
    const host = express.querySelector("shopify-accelerated-checkout-cart");
    if (!host) return;
    const wallets = Array.from(host.children).filter(child => child.tagName.includes("BUTTON") && (child.getAttribute("slot") || "").startsWith("button-") && !child.hidden);
    const count = Math.max(1, wallets.length);
    const capacity = Math.max(1, Math.floor((host.clientWidth + 6) / 156));
    const columns = count === 4 && capacity === 3 ? 2 : Math.min(count, capacity);
    const walletWidth = Math.max(150, (host.clientWidth - (columns - 1) * 6) / columns);
    host.style.setProperty("--fr-wallet-width", `${walletWidth}px`);
    host.style.setProperty("--fr-wallet-columns", columns);
    host.style.setProperty("--fr-wallet-height", `${Math.ceil(count / columns) * 46 - 6}px`);
    wallets.forEach((wallet, index) => {
      wallet.classList.add("fr-cart-wallet");
      wallet.style.setProperty("--fr-wallet-column", index % columns);
      wallet.style.transform = "none";
    });
    const origin = host.getBoundingClientRect();
    const rects = wallets.map(wallet => wallet.getBoundingClientRect());
    wallets.forEach((wallet, index) => {
      const x = origin.left + (index % columns) * (walletWidth + 6) - rects[index].left;
      const y = origin.top + Math.floor(index / columns) * 46 - rects[index].top;
      wallet.style.transform = `translate(${x}px,${y}px)`;
    });
  }
  window.addEventListener("resize", layoutExpressWallets);
  const expressContainer = document.getElementById("fr-cart-express");
  if (expressContainer) {
    new MutationObserver(layoutExpressWallets).observe(expressContainer, { childList: true, subtree: true });
  }

  function renderPurchaseGuidance(total) {
    const labels = CONFIG.labels || {};
    const message = document.getElementById("fr-cart-delivery-message");
    const fill = document.getElementById("fr-cart-delivery-fill");
    const threshold = Number(CONFIG.freeShippingThreshold) || 4000;
    const included = cartState.items.some(isDiscoveryPack);
    const unlocked = included || total >= threshold;
    const text = included ? labels.delivery_included : unlocked ? labels.delivery_free : (labels.delivery_remaining || "").replace("[amount]", new Intl.NumberFormat("en-GB", { style: "currency", currency: CONFIG.currency || "GBP", minimumFractionDigits: 0, maximumFractionDigits: 2 }).format((threshold - total) / 100));
    if (message) message.textContent = text;
    if (fill) fill.style.width = (unlocked ? 100 : Math.max(0, Math.min(100, total / threshold * 100))) + "%";
    const strip = document.getElementById("fr-cart-delivery");
    if (strip) strip.classList.toggle("is-unlocked", unlocked);
    const eligible = new Set(CONFIG.eligibleBottleVariants || []);
    const bottleCount = cartState.items.reduce((count, item) => count + (!isGiftItem(item) && !isDiscoveryLine(item) && eligible.has(Number(item.variant_id || item.id)) ? item.quantity : 0), 0);
    const tiers = CONFIG.bundleTiers || [];
    const next = tiers.find(tier => bottleCount < tier.quantity);
    const current = tiers.filter(tier => bottleCount >= tier.quantity).pop();
    const currentQuantity = current ? current.quantity : bottleCount >= 1 ? 1 : 0;
    const rail = drawer.querySelector('.fr-cart-rewards__steps');
    if (rail) rail.style.setProperty('--fr-reward-fill', `${Math.min(4, Math.max(0, bottleCount - 1)) * 20}%`);
    drawer.querySelectorAll('[data-fr-tier]').forEach(step => {
      const quantity = Number(step.dataset.frTier);
      step.classList.toggle('is-reached', bottleCount >= quantity);
      if (quantity === currentQuantity) step.setAttribute('aria-current', 'step');
      else step.removeAttribute('aria-current');
    });
    const bundleMessage = document.getElementById('fr-cart-bundle-message');
    if (bundleMessage) {
      const remaining = next ? next.quantity - bottleCount : 0;
      const position = bottleCount > 0 ? (bottleCount === 1 ? labels.bundle_count_one : labels.bundle_count.replace('[count]', bottleCount)) + ' ' : '';
      bundleMessage.textContent = position + (next ? (remaining === 1 ? labels.bundle_next_one : labels.bundle_next).replace('[count]', remaining).replace('[percent]', next.percent) : labels.bundle_best);
    }
  }

  function renderShippingBar(total) {
    if (!shippingBar || !cartState) return;
    const { bottleQty, sampleQty } = classifyCart(cartState.items);
    shippingBar.classList.remove(
      "fr-cart__shipping-bar--complete",
      "fr-bundle-bar--step2",
      "fr-bundle-bar--step3",
      "fr-bundle-bar--sample",
    );
    if (cartState.items.some(isDiscoveryPack) && bottleQty === 0 && sampleQty === 0) {
      if (shippingText) shippingText.textContent = "Your discovery set · 6 × 2ml · £10";
      if (shippingFill) shippingFill.style.width = "100%";
      const picker = document.getElementById("fr-bundle-picker");
      if (picker) picker.classList.add("fr-is-hidden");
    } else if (bottleQty > 0) {
      renderBottleProgress(bottleQty);
      updateUpsellLabel(bottleQty, 0);
      showBundlePicker(bottleQty);
    } else if (sampleQty > 0) {
      renderSampleProgress(sampleQty);
      updateUpsellLabel(0, sampleQty);
      showSamplePicker(sampleQty);
    }
    renderFreeShippingStrip(total);
  }

  function setMarkers(pos1, pos2) {
    const m1 = document.getElementById("fr-marker-1");
    const m2 = document.getElementById("fr-marker-2");
    if (m1) m1.style.left = pos1;
    if (m2) m2.style.left = pos2;
  }

  function renderBottleProgress(qty) {
    setMarkers("33.33%", "66.66%");
    if (qty === 1) {
      if (shippingFill) shippingFill.style.width = "33%";
      if (shippingText)
        shippingText.innerHTML =
          "Add <strong>1 More</strong> for <strong>\u00a320</strong>";
    } else if (qty === 2) {
      shippingBar.classList.add("fr-bundle-bar--step2");
      if (shippingFill) shippingFill.style.width = "66%";
      if (shippingText)
        shippingText.innerHTML =
          "Make it a trio for just <strong>\u00a310 more</strong> \u2014 You save <strong>\u00a330</strong>";
    } else {
      shippingBar.classList.add(
        "fr-cart__shipping-bar--complete",
        "fr-bundle-bar--step3",
      );
      if (shippingFill) shippingFill.style.width = "100%";
      if (shippingText)
        shippingText.innerHTML =
          "<strong>Bundle Unlocked: Buy 2, Get 1 Free</strong>";
    }
  }

  function renderSampleProgress(qty) {
    shippingBar.classList.add("fr-bundle-bar--sample");
    setMarkers("60%", "100%");
    if (qty === 1) {
      if (shippingFill) shippingFill.style.width = "20%";
      if (shippingText)
        shippingText.innerHTML =
          "Add <strong>2 more</strong> for just <strong>\u00a37</strong>";
    } else if (qty === 2) {
      if (shippingFill) shippingFill.style.width = "40%";
      if (shippingText)
        shippingText.innerHTML =
          "Add <strong>1 more</strong> for just <strong>\u00a32</strong>";
    } else if (qty === 3) {
      shippingBar.classList.add("fr-bundle-bar--step2");
      if (shippingFill) shippingFill.style.width = "60%";
      if (shippingText)
        shippingText.innerHTML =
          "<strong>Bundle Unlocked</strong><br>Add another <strong>2</strong> for just <strong>\u00a33</strong>";
    } else if (qty === 4) {
      shippingBar.classList.add("fr-bundle-bar--step2");
      if (shippingFill) shippingFill.style.width = "80%";
      if (shippingText)
        shippingText.innerHTML =
          "<strong>Bundle Unlocked</strong><br>Add <strong>1 more</strong> to complete your <strong>\u00a315 bundle</strong>";
    } else {
      shippingBar.classList.add(
        "fr-cart__shipping-bar--complete",
        "fr-bundle-bar--step3",
      );
      if (shippingFill) shippingFill.style.width = "100%";
      if (shippingText)
        shippingText.innerHTML =
          "<strong>5-Sample Bundle Unlocked \u2014 Best Value!</strong>";
    }
  }

  function renderFreeShippingStrip(total) {
    const strip = document.getElementById("fr-free-ship-bar");
    const shipFill = document.getElementById("fr-ship-fill");
    const shipText = document.getElementById("fr-ship-text");
    if (!strip) return;
    if (cartState?.items.some(isDiscoveryPack)) {
      strip.classList.add("is-unlocked");
      if (shipFill) shipFill.style.width = "100%";
      if (shipText) shipText.textContent = "Royal Mail Tracked 48 delivery included with your discovery set";
      return;
    }
    const threshold = CONFIG.freeShippingThreshold;
    const pct = Math.min(100, Math.round((total / threshold) * 100));
    if (shipFill) shipFill.style.width = pct + "%";
    if (total >= threshold) {
      if (!strip.classList.contains("is-unlocked")) {
        strip.classList.add("is-unlocked", "fr-ship-pulse");
        setTimeout(() => strip.classList.remove("fr-ship-pulse"), 1200);
      }
      if (shipText)
        shipText.innerHTML =
          '<svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M1.5 6L4.5 9L10.5 3" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
          " <strong>Free UK shipping unlocked!</strong>";
    } else {
      strip.classList.remove("is-unlocked");
      if (shipText)
        shipText.innerHTML =
          "Add <strong>" +
          formatMoney(threshold - total) +
          "</strong> more for free UK shipping";
    }
  }

  function calculateBottleDiscount(bottleQty) {
    if (bottleQty >= 3) return bottleQty * 1000;
    if (bottleQty === 2) return 1000;
    return 0;
  }

  function renderOrderSummary(total, discount, paidOriginal) {
    const standardShipping = CONFIG.standardShipping || 295;
    const threshold = CONFIG.freeShippingThreshold;
    const originalTotal =
      paidOriginal || total + discount;
    const displayDiscount = discount;
    // Shopify's total_price is already the authoritative discounted subtotal.
    const netTotal = total;
    const shippingCost = cartState?.items.some(isDiscoveryPack) || netTotal >= threshold ? 0 : standardShipping;
    const grandTotal = netTotal + shippingCost;

    const origRow = document.getElementById("fr-summary-original-row");
    const origVal = document.getElementById("fr-summary-original-val");
    if (displayDiscount > 0) {
      if (origRow) origRow.classList.remove("fr-is-hidden");
      if (origVal) origVal.textContent = formatMoney(originalTotal);
    } else {
      if (origRow) origRow.classList.add("fr-is-hidden");
    }

    const saveRow = document.getElementById("fr-summary-saving-row");
    const saveVal = document.getElementById("fr-summary-saving-val");
    if (displayDiscount > 0) {
      if (saveRow) saveRow.classList.remove("fr-is-hidden");
      if (saveVal)
        saveVal.textContent = "\u2212" + formatMoney(displayDiscount);
    } else {
      if (saveRow) saveRow.classList.add("fr-is-hidden");
    }

    const shipRow = document.getElementById("fr-summary-shipping-row");
    const shipVal = document.getElementById("fr-summary-shipping-val");
    const shipSublbl = document.getElementById("fr-shipping-sublabel");
    if (shipRow) shipRow.classList.remove("fr-is-hidden");
    if (shippingCost === 0) {
      if (shipVal) {
        shipVal.textContent = CONFIG.labels.free;
        shipVal.className = "fr-cart__summary-val fr-cart__summary-val--free";
      }
      if (shipSublbl) shipSublbl.textContent = CONFIG.labels.tracked_48;
    } else {
      if (shipVal) {
        shipVal.textContent = formatMoney(shippingCost);
        shipVal.className = "fr-cart__summary-val";
      }
      if (shipSublbl) shipSublbl.textContent = CONFIG.labels.tracked_48;
    }

    if (subtotalEl) subtotalEl.textContent = formatMoney(grandTotal);
    const compactTotal = document.getElementById("fr-compact-total");
    if (compactTotal) compactTotal.textContent = formatMoney(grandTotal);
    const checkoutPrice = document.getElementById("fr-checkout-price");
    if (checkoutPrice) checkoutPrice.textContent = formatMoney(grandTotal);
  }

  /* ─── BUNDLE PICKER ──────────────────────────────────────── */
  const PICKER_COLS = (window.FRCartConfig || {}).pickerCollections || {
    all: "all",
    women: "womens",
    men: "best-aftershaves-for-men",
    unisex: "unisex",
  };
  let pickerCache = {};
  let pickerFilter = "all";
  let pickerReady = false;
  let pickerMode = null;
  let pickerExpanded = false;

  function isSampleItem(item) {
    const v = (item.variant_title || "").toLowerCase();
    return (
      /(?<!\d)5ml(?!\d)/i.test(v) || v.includes("sample") || item.price <= 1499
    );
  }

  function showBundlePicker(bottleQty) {
    const picker = document.getElementById("fr-bundle-picker");
    const footer = document.getElementById("fr-cart-footer");
    if (!picker) return;
    if (bottleQty < 1 || bottleQty >= 3) {
      pickerExpanded = false;
      picker.classList.add("fr-is-hidden");
      if (footer) footer.classList.remove("fr-cart__footer--compact");
      if (shippingBar) {
        shippingBar.classList.remove("fr-shipping-bar--clickable");
        shippingBar.classList.remove("fr-bundle-offer--expanded");
        shippingBar.removeAttribute("role");
        shippingBar.removeAttribute("tabindex");
        shippingBar.removeAttribute("aria-controls");
        shippingBar.removeAttribute("aria-expanded");
        shippingBar.removeAttribute("aria-label");
      }
      return;
    }
    if (pickerMode !== "bottle") {
      pickerMode = "bottle";
      pickerReady = false;
      pickerCache = {};
      pickerExpanded = false;
    }
    if (pickerExpanded) picker.classList.remove("fr-is-hidden");
    else picker.classList.add("fr-is-hidden");
    if (footer) footer.classList.remove("fr-cart__footer--compact");
    if (shippingBar) {
      shippingBar.classList.add("fr-shipping-bar--clickable");
      if (pickerExpanded)
        shippingBar.classList.add("fr-bundle-offer--expanded");
      else shippingBar.classList.remove("fr-bundle-offer--expanded");
      shippingBar.setAttribute("role", "button");
      shippingBar.setAttribute("tabindex", "0");
      shippingBar.setAttribute("aria-controls", "fr-bundle-picker");
      shippingBar.setAttribute("aria-expanded", String(pickerExpanded));
      shippingBar.setAttribute(
        "aria-label",
        pickerExpanded ? "Hide bundle fragrance picker" : "Pick your bundle",
      );
    }
    if (pickerExpanded && !pickerReady) {
      pickerReady = true;
      setPickerFilter(getFirstItemGender(false));
    }
  }

  function showSamplePicker(sampleQty) {
    const picker = document.getElementById("fr-bundle-picker");
    const footer = document.getElementById("fr-cart-footer");
    if (!picker) return;
    if (sampleQty < 1 || sampleQty >= 5) {
      pickerExpanded = false;
      picker.classList.add("fr-is-hidden");
      if (footer) footer.classList.remove("fr-cart__footer--compact");
      if (shippingBar) {
        shippingBar.classList.remove("fr-shipping-bar--clickable");
        shippingBar.classList.remove("fr-bundle-offer--expanded");
        shippingBar.removeAttribute("role");
        shippingBar.removeAttribute("tabindex");
        shippingBar.removeAttribute("aria-controls");
        shippingBar.removeAttribute("aria-expanded");
        shippingBar.removeAttribute("aria-label");
      }
      return;
    }
    if (pickerMode !== "sample") {
      pickerMode = "sample";
      pickerReady = false;
      pickerCache = {};
      pickerExpanded = false;
    }
    if (pickerExpanded) picker.classList.remove("fr-is-hidden");
    else picker.classList.add("fr-is-hidden");
    if (footer) footer.classList.remove("fr-cart__footer--compact");
    if (shippingBar) {
      shippingBar.classList.add("fr-shipping-bar--clickable");
      if (pickerExpanded)
        shippingBar.classList.add("fr-bundle-offer--expanded");
      else shippingBar.classList.remove("fr-bundle-offer--expanded");
      shippingBar.setAttribute("role", "button");
      shippingBar.setAttribute("tabindex", "0");
      shippingBar.setAttribute("aria-controls", "fr-bundle-picker");
      shippingBar.setAttribute("aria-expanded", String(pickerExpanded));
      shippingBar.setAttribute(
        "aria-label",
        pickerExpanded ? "Hide bundle fragrance picker" : "Pick your bundle",
      );
    }
    if (pickerExpanded && !pickerReady) {
      pickerReady = true;
      setPickerFilter(getFirstItemGender(true));
    }
  }

  function placeBundleOfferAboveCheckout() {
    if (!footerEl || !shippingBar) return;
    const checkoutLink = document.getElementById("fr-cart-checkout");
    const checkoutWrap =
      footerEl.querySelector(".fr-cart__checkout-wrap") ||
      (checkoutLink && checkoutLink.parentElement);
    if (!checkoutWrap) return;
    footerEl.insertBefore(shippingBar, checkoutWrap);
    shippingBar.classList.add("fr-bundle-offer");
  }

  function toggleBundlePicker() {
    if (
      !shippingBar ||
      !shippingBar.classList.contains("fr-shipping-bar--clickable")
    )
      return;

    pickerExpanded = !pickerExpanded;
    const { bottleQty, sampleQty } = classifyCart(
      cartState ? cartState.items : [],
    );
    if (bottleQty > 0) showBundlePicker(bottleQty);
    else if (sampleQty > 0) showSamplePicker(sampleQty);

    if (pickerExpanded) {
      const picker = document.getElementById("fr-bundle-picker");
      const cartBody = document.getElementById("fr-cart-body");
      setTimeout(() => {
        if (!picker || !cartBody) return;
        const bodyRect = cartBody.getBoundingClientRect();
        const pickerRect = picker.getBoundingClientRect();
        cartBody.scrollBy({
          top: pickerRect.top - bodyRect.top - 12,
          behavior: "smooth",
        });
      }, 0);
    }
  }

  function getFirstItemGender(preferSample) {
    if (!cartState) return "all";
    const item = cartState.items.find((i) =>
      preferSample ? isSampleItem(i) : !isSampleItem(i),
    );
    if (!item) return "all";
    const prop =
      item.properties && item.properties._gender
        ? item.properties._gender.toLowerCase()
        : "";
    const meta = window.FRP_META && window.FRP_META[item.product_id];
    const g = prop || (meta && meta.g ? meta.g.toLowerCase() : "");
    if (g.includes("women") || g.includes("woman")) return "women";
    if (g.includes("men") || g.includes("man")) return "men";
    if (g.includes("unisex")) return "unisex";
    return "all";
  }

  function setPickerFilter(handle) {
    pickerFilter = handle;
    document.querySelectorAll(".fr-bpf").forEach((btn) => {
      btn.classList.toggle("is-active", btn.dataset.handle === handle);
    });
    loadPickerProducts(handle);
  }

  async function loadPickerProducts(handle) {
    const grid = document.getElementById("fr-bp-grid");
    if (!grid) return;
    grid.innerHTML = Array(4)
      .fill(
        '<div class="fr-bp-skeleton"><div class="fr-bp-skel-img fr-skel"></div>' +
          '<div class="fr-bp-skel-info"><div class="fr-skel" style="height:10px;margin-bottom:5px;width:80%"></div>' +
          '<div class="fr-skel" style="height:9px;width:55%"></div></div></div>',
      )
      .join("");
    const cacheKey = handle + ":" + (pickerMode || "bottle");
    if (!pickerCache[cacheKey]) {
      try {
        const colHandle = PICKER_COLS[handle] || "all";
        const url = "/collections/" + colHandle + "/products.json?limit=250";
        const res = await fetch(url);
        if (!res.ok) throw new Error("HTTP " + res.status + " for " + url);
        const data = await res.json();
        pickerCache[cacheKey] = data.products || [];
        if (!pickerCache[cacheKey].length) {
          grid.innerHTML =
            '<p class="fr-bp-empty">No fragrances found in this range.</p>';
          return;
        }
      } catch (e) {
        console.error("[FR Picker]", e);
        grid.innerHTML =
          '<p class="fr-bp-error">Could not load products. <button class="fr-bp-retry" type="button">Try again</button></p>';
        return;
      }
    }
    renderPickerGrid(pickerCache[cacheKey], grid);
  }

  function getImgSrc(product, size) {
    let raw = null;
    if (product.images && product.images.length) raw = product.images[0].src;
    else if (product.featured_image) {
      raw =
        typeof product.featured_image === "string"
          ? product.featured_image
          : product.featured_image.src || null;
    }
    if (!raw) return "";
    return raw.split("?")[0].replace(/\.(\w+)$/, "_" + size + ".$1");
  }

  function getPickerVariant(product, isSample) {
    const variants = (product && product.variants) || [];
    if (isSample) {
      return variants.find(
        (variant) =>
          variant.available !== false &&
          /(?<!\d)5ml(?!\d)/i.test(variant.title || ""),
      );
    }
    return variants.find(
      (variant) =>
        variant.available !== false && parseFloat(variant.price) > 14.99,
    );
  }

  function renderPickerGrid(products, grid) {
    const inCart = new Set(
      (cartState ? cartState.items : []).map((i) => i.product_id),
    );
    const isSample = pickerMode === "sample";
    const filtered = products.filter((p) => {
      if (inCart.has(p.id)) return false;
      return !!getPickerVariant(p, isSample);
    });
    if (!filtered.length) {
      grid.innerHTML =
        '<p class="fr-bp-empty">All fragrances are already in your bag.</p>';
      return;
    }
    grid.innerHTML = filtered
      .map((product) => {
        const variant = getPickerVariant(product, isSample);
        if (!variant) return "";
        const vendor = product.vendor || "";
        const inspiredHtml = vendor.toLowerCase().startsWith("inspired by")
          ? "Inspired by <strong>" +
            escHtml(vendor.replace(/^inspired by /i, "")) +
            "</strong>"
          : escHtml(vendor);
        const imgSrc = getImgSrc(product, "300x300");
        const meta = window.FRP_META && window.FRP_META[product.id];
        const stars = buildStars(
          meta && meta.rv ? parseFloat(meta.rv) : 0,
          meta && meta.rc ? parseInt(meta.rc) : 0,
        );
        return `
        <div class="fr-bp-card">
          <div class="fr-bp-card__img-wrap">
            ${imgSrc ? `<img src="${imgSrc}" alt="${escHtml(product.title)}" class="fr-bp-card__img" loading="lazy">` : ""}
          </div>
          <div class="fr-bp-card__info">
            <div class="fr-bp-card__stars">${stars}</div>
            <p class="fr-bp-card__title">${escHtml(product.title)}</p>
            <p class="fr-bp-card__insp">${inspiredHtml}</p>
            <button class="fr-bp-card__add fr-btn fr-btn--pill" data-variant-id="${variant.id}" data-product-id="${product.id}" type="button">Add</button>
          </div>
        </div>`;
      })
      .join("");
  }

  function updateUpsellLabel(bottleQty, sampleQty) {
    const label = document.getElementById("fr-upsell-label");
    if (!label) return;
    if (bottleQty === 1)
      label.innerHTML =
        "Add 1 more for just <strong>\u00a320</strong> \u2014 you save \u00a310";
    else if (bottleQty === 2)
      label.innerHTML =
        "Add 1 more for just <strong>\u00a310</strong> \u2014 you save \u00a330 total";
    else if (sampleQty === 1)
      label.innerHTML =
        "Add <strong>2 more</strong> for just <strong>\u00a37</strong>";
    else if (sampleQty === 2)
      label.innerHTML =
        "Add <strong>1 more</strong> for just <strong>\u00a32</strong>";
    else if (sampleQty === 3)
      label.innerHTML =
        "<strong>Bundle Unlocked</strong><br>Add another <strong>2</strong> for just <strong>\u00a33</strong>";
    else if (sampleQty === 4)
      label.innerHTML =
        "<strong>Bundle Unlocked</strong><br>Add <strong>1 more</strong> to complete your <strong>\u00a315 bundle</strong>";
    else label.textContent = "You might also like";
  }

  /* ─── RENDER LINE ITEMS ───────────────────────────────────── */
  function renderItems(items) {
    if (!itemsEl) return;

    const discoverySamples = items.filter(item => isDiscoveryLine(item) && !isDiscoveryPack(item));
    const regularItems = items.filter(item => !isGiftItem(item) && (!isDiscoveryLine(item) || isDiscoveryPack(item)));
    // Sort gift items: 2ml samples first (by product title), gift bag last
    const giftItems = items.filter(isGiftItem).sort((a, b) => {
      const aIsBag = a.variant_id === 58146955166080;
      const bIsBag = b.variant_id === 58146955166080;
      if (aIsBag && !bIsBag) return 1;
      if (!aIsBag && bIsBag) return -1;
      return a.product_title.localeCompare(b.product_title);
    });

    // Render regular line items
    const itemsHtml = regularItems
      .map((item) => {
        const itemImage = item.image || (isDiscoveryPack(item) ? discoverySamples.find(sample => sample.properties?._fr_discovery_group === item.properties?._fr_discovery_group)?.image : "");
        const imgSrc = itemImage
          ? itemImage.replace(/(\.\w+)(\?.*)?$/, "_160x160$1")
          : "";

        const imgHtml = imgSrc
          ? `<img src="${imgSrc}" alt="${escHtml(item.title)}" class="fr-cart-item__img" loading="lazy" width="80" height="80">`
          : "";

        const compareHtml =
          item.original_line_price > item.final_line_price
            ? `<span class="fr-cart-item__price--compare">${formatMoney(item.original_line_price)}</span>`
            : "";

        const variantHtml =
          item.variant_title && item.variant_title !== "Default Title"
            ? `<p class="fr-cart-item__variant">${escHtml(item.variant_title)}</p>`
            : "";

        const catalogueProduct = window.FRFragranceSearch?.products.find(product => product.handle === item.handle);
        const inspiredBy = catalogueProduct?.inspiration
          ? (catalogueProduct.inspiration.toLowerCase().startsWith('inspired by') ? catalogueProduct.inspiration : 'Inspired by ' + catalogueProduct.inspiration)
          : item.properties && item.properties["_inspired_by"]
            ? item.properties["_inspired_by"]
            : item.vendor && item.vendor !== "Fragrance Republic"
              ? item.vendor
              : "";

        const inspiredHtml = inspiredBy
          ? `<p class="fr-cart-item__inspired">${escHtml(inspiredBy)}</p>`
          : "";

        const rating =
          item.properties && item.properties["_rating"]
            ? parseFloat(item.properties["_rating"])
            : null;
        const ratingCount =
          item.properties && item.properties["_rating_count"]
            ? parseInt(item.properties["_rating_count"])
            : null;
        const starsHtml = rating ? buildStars(rating, ratingCount) : "";

        return `
        <div class="fr-cart-item" data-key="${item.key}" data-handle="${item.handle}">
          <a class="fr-cart-item__img-wrap" href="${escHtml(item.url)}" aria-label="${escHtml(item.product_title)}">${imgHtml}</a>
          <div class="fr-cart-item__info">
            <div>
              ${starsHtml}
              <a class="fr-cart-item__title" href="${escHtml(item.url)}">${escHtml(item.product_title)}</a>
              ${inspiredHtml}
              ${variantHtml}
              ${isDiscoveryPack(item) ? `<p class="fr-cart-item__variant">Six 2ml sprays</p>` : ""}
            </div>
            <div class="fr-cart-item__bottom">
              <div class="fr-cart-item__price-wrap">
                ${compareHtml}
                <span class="fr-cart-item__price">${formatMoney(item.final_line_price)}</span>
              </div>
              ${isDiscoveryPack(item) ? "" : `<div class="fr-cart-item__qty">
                <button class="fr-cart-item__qty-btn" data-action="decrease" data-key="${item.key}" aria-label="Decrease quantity">−</button>
                <span class="fr-cart-item__qty-val">${item.quantity}</span>
                <button class="fr-cart-item__qty-btn" data-action="increase" data-key="${item.key}" aria-label="Increase quantity">+</button>
              </div>`}
            </div>
          </div>
          <button class="fr-cart-item__remove" data-key="${item.key}" aria-label="Remove ${escHtml(item.title)}">Remove</button>
        </div>
      `;
      })
      .join("");

    // Render compact gifts panel
    let giftsHtml = "";
    if (giftItems.length) {
      const giftRows = giftItems
        .map((item) => {
          const imgSrc = item.image
            ? item.image.replace(/(\.\w+)(\?.*)?$/, "_80x80$1")
            : "";
          const imgHtml = imgSrc
            ? `<img src="${imgSrc}" alt="${escHtml(item.product_title)}" class="fr-gifts__img" width="40" height="40" loading="lazy">`
            : `<div class="fr-gifts__img fr-gifts__img--placeholder"></div>`;
          const label =
            item.variant_title && item.variant_title !== "Default Title"
              ? `${escHtml(item.product_title)} <span class="fr-gifts__variant">${escHtml(item.variant_title)}</span>`
              : escHtml(item.product_title);
          const qty = item.quantity > 1 ? ` ×${item.quantity}` : "";
          return `
          <div class="fr-gifts__row">
            ${imgHtml}
            <span class="fr-gifts__label">${label}${qty}</span>
            <span class="fr-gifts__free">Free</span>
          </div>`;
        })
        .join("");

      const giftTypes = [];
      if (giftItems.some(item => /2\s*ml/i.test(item.variant_title || item.title || ""))) giftTypes.push(CONFIG.labels.gift_testers);
      if (giftItems.some(item => item.variant_id === 58146955166080)) giftTypes.push(CONFIG.labels.gift_bags);
      const giftLabel = giftTypes.length ? giftTypes.join(" + ") : CONFIG.labels.gifts_included;
      giftsHtml = `
        <details class="fr-gifts-compact">
          <summary>${escHtml(giftLabel)}</summary>
          <div class="fr-gifts-compact__details">${giftRows}</div>
        </details>`;
    }

    const discoveryHtml = discoverySamples.length ? `<div class="fr-gifts-panel"><div class="fr-gifts__header">Your six scents</div>${discoverySamples.map(item => `<div class="fr-gifts__row">${item.image ? `<img src="${escHtml(item.image)}" alt="" class="fr-gifts__img" width="40" height="40" loading="lazy">` : ""}<span class="fr-gifts__label">${escHtml(item.product_title)} <span class="fr-gifts__variant">2ml</span></span><span class="fr-gifts__free">Included</span></div>`).join("")}</div>` : "";
    itemsEl.innerHTML = itemsHtml + discoveryHtml + giftsHtml;
  }

  function buildStars(score, count) {
    const value = Math.min(5, Math.max(0, Number(score) || 0));
    if (!value) return '';
    const label = (drawer.dataset.frRatingLabel || '[rating] / 5').replace('[rating]', value.toFixed(1));
    return `<span class="fr-cart-score" role="img" aria-label="${escHtml(label)}"><span class="fr-cart-score__stars" style="--fr-cart-rating-fill:${value * 20}%" aria-hidden="true"><span>★★★★★</span><span>★★★★★</span></span><span aria-hidden="true">${value.toFixed(1).replace(/\.0$/, '')}</span></span>`;
  }

  function updateBadges(count) {
    if (countBadge) {
      countBadge.textContent = count;
      countBadge.style.display = count === 0 ? "none" : "";
    }
    document
      .querySelectorAll(
        "[data-cart-count], .cart-count, .js-cart-count, [data-header-cart-count]",
      )
      .forEach((el) => {
        el.textContent = count;
      });
    document.querySelectorAll(".fr-cart-badge").forEach((el) => {
      el.style.display = count === 0 ? "none" : "flex";
    });
    document.querySelectorAll(".fr-mobile-header .cart-link__bubble").forEach((el) => {
      el.classList.toggle("cart-link__bubble--visible", count > 0);
    });
    document.querySelectorAll(".cart__bubble").forEach((el) => {
      el.textContent = count;
      el.style.display = count === 0 ? "none" : "";
    });
  }

  async function changeQty(key, newQty) {
    if (isUpdating) return;
    const willBeEmpty =
      getCustomerItemCount(cartState ? cartState.items : [], key, newQty) === 0;
    if (willBeEmpty) renderEmptyCartState();
    isUpdating = true;
    setCheckoutAvailable(false);
    const errorEl = document.getElementById("fr-cart-error");
    if (errorEl) errorEl.classList.add("fr-is-hidden");
    if (itemsEl) itemsEl.classList.add("is-updating");
    try {
      const changed = cartState?.items.find(item => item.key === key);
      const discoveryRemoval = changed && isDiscoveryLine(changed) && newQty === 0;
      const updates = discoveryRemoval ? Object.fromEntries(cartState.items.filter(item => isDiscoveryLine(item) && item.properties._fr_discovery_group === changed.properties._fr_discovery_group).map(item => [item.key, 0])) : null;
      const res = await fetch(discoveryRemoval ? "/cart/update.js" : "/cart/change.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: JSON.stringify(discoveryRemoval ? { updates } : { id: key, quantity: newQty }),
        credentials: "include",
      });
      if (!res.ok) throw new Error("Cart change failed");
      cartState = await res.json();
      renderCart();
      dispatch("fr:cart:updated", cartState);
    } catch (err) {
      console.error("[FR Cart]", err);
      if (willBeEmpty) renderCart();
      if (errorEl) { errorEl.textContent = CONFIG.labels.update_error; errorEl.classList.remove("fr-is-hidden"); }
    } finally {
      isUpdating = false;
      setCheckoutAvailable(getCustomerItemCount(cartState ? cartState.items : []) > 0);
      if (itemsEl) itemsEl.classList.remove("is-updating");
    }
  }

  async function addToCart(variantId, qty = 1, properties = {}) {
    try {
      const res = await fetch("/cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity: qty, properties }],
        }),
        credentials: "include",
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.description || "Add to cart failed");
      }
      openCartShell();
      await fetchCart();
      dispatch("fr:cart:item-added", {
        variantId,
        qty,
        cart: cartState,
      });
      return true;
    } catch (err) {
      console.error("[FR Cart]", err);
      return false;
    }
  }

  /* ─── EVENTS ──────────────────────────────────────────────── */

  document.addEventListener("click", (e) => {
    if (e.target.closest(".fr-bp-retry")) {
      delete pickerCache[pickerFilter + ":" + (pickerMode || "bottle")];
      loadPickerProducts(pickerFilter);
      return;
    }
    const tab = e.target.closest(".fr-bpf");
    if (!tab) return;
    setPickerFilter(tab.dataset.handle);
  });

  document.addEventListener("click", async (e) => {
    const btn = e.target.closest(".fr-bp-card__add");
    if (!btn) return;
    const variantId = btn.dataset.variantId;
    if (!variantId) return;
    btn.disabled = true;
    btn.textContent = "...";
    const ok = await addToCart(variantId, 1);
    if (ok) {
      const cacheKey = pickerFilter + ":" + (pickerMode || "bottle");
      const grid = document.getElementById("fr-bp-grid");
      if (grid && pickerCache[cacheKey]) {
        renderPickerGrid(pickerCache[cacheKey], grid);
      }
    } else {
      btn.disabled = false;
      btn.textContent = "Add";
    }
  });

  const backBtn = document.getElementById("fr-cart-back");
  if (backBtn) backBtn.addEventListener("click", closeCart);
  if (closeBtn) closeBtn.addEventListener("click", closeCart);
  if (overlay) overlay.addEventListener("click", closeCart);

  document.addEventListener("keydown", (e) => {
    if (!isOpen) return;
    if (e.key === "Escape") { e.preventDefault(); closeCart(); return; }
    if (e.key === "Tab") {
      const focusable = Array.from(drawer.querySelectorAll('a[href],button:not([disabled]),input:not([disabled]),[tabindex="0"]')).filter(el => el.getClientRects().length && !el.closest('[hidden],.fr-is-hidden'));
      const first = focusable[0], last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  });

  const deliveryContinue = document.getElementById("fr-cart-delivery-continue");
  if (deliveryContinue) deliveryContinue.addEventListener("click", closeCart);
  if (continueShopping) continueShopping.addEventListener("click", closeCart);
  document.getElementById('fr-cart-retry')?.addEventListener('click', fetchCart);
  if (continueBrowsing) continueBrowsing.addEventListener("click", closeCart);
  if (viewBagBtn)
    viewBagBtn.addEventListener("click", () => {
      window.location.href = CONFIG.cartUrl;
    });

  document.addEventListener("click", (e) => {
    const cartLink = e.target.closest(
      'a[href="/cart"], [data-cart-toggle], [data-open-cart], .js-drawer-open-right, [aria-controls="cart-drawer"]',
    );
    if (cartLink) {
      e.preventDefault();
      openCart();
      return;
    }
    const headerCart = e.target.closest(
      ".site-header__cart, .header__icon--cart, [data-cart-count], .cart-toggle",
    );
    if (headerCart) {
      const tag = headerCart.tagName.toLowerCase();
      if (tag === "a" || tag === "button") {
        e.preventDefault();
        openCart();
      }
    }
  });

  document.addEventListener("submit", async (e) => {
    const form = e.target;
    if (!form.matches('[action="/cart/add"]')) return;
    e.preventDefault();
    const fd = new FormData(form);
    const variantId = fd.get("id");
    const qty = parseInt(fd.get("quantity") || "1", 10);
    const properties = {};
    for (const [k, v] of fd.entries()) {
      if (k.startsWith("properties[")) {
        const key = k.replace(/^properties\[/, "").replace(/]$/, "");
        properties[key] = v;
      }
    }
    const btn = form.querySelector('[type="submit"]');
    if (btn) {
      btn.disabled = true;
      btn.textContent = "Adding...";
    }
    await addToCart(variantId, qty, properties);
    if (btn) {
      btn.disabled = false;
      btn.textContent = btn.dataset.originalText || "Add to Cart";
    }
  });

  document.addEventListener("click", async (e) => {
    const atcBtn = e.target.closest(
      '[data-add-to-cart]:not([type="submit"]), .js-add-to-cart',
    );
    if (!atcBtn) return;
    e.preventDefault();
    const variantId =
      atcBtn.dataset.variantId ||
      atcBtn.closest("[data-variant-id]")?.dataset.variantId ||
      document.querySelector('[name="id"]')?.value;
    if (!variantId) return;
    atcBtn.disabled = true;
    await addToCart(variantId, 1);
    atcBtn.disabled = false;
  });

  document.addEventListener("click", (e) => {
    const btn = e.target.closest(".fr-cart-item__qty-btn");
    if (!btn) return;
    const key = btn.dataset.key;
    const action = btn.dataset.action;
    const item = cartState?.items.find((i) => i.key === key);
    if (!item) return;
    if (action === "increase") changeQty(key, item.quantity + 1);
    else if (action === "decrease")
      changeQty(key, Math.max(0, item.quantity - 1));
  });

  document.addEventListener("click", (e) => {
    const btn = e.target.closest(".fr-cart-item__remove");
    if (!btn) return;
    changeQty(btn.dataset.key, 0);
  });

  document.addEventListener("click", async (e) => {
    const btn = e.target.closest(".fr-upsell-card__add");
    if (!btn) return;
    const variantId = btn.dataset.variantId;
    if (!variantId) return;
    btn.disabled = true;
    btn.textContent = "...";
    const success = await addToCart(variantId, 1);
    if (success) {
      btn.textContent = "✓ Added";
      btn.style.background = "#555";
    } else {
      btn.disabled = false;
      btn.textContent = "Add";
    }
  });

  document.addEventListener("click", async (e) => {
    const btn = e.target.closest(".fr-addon-item__toggle");
    if (!btn) return;
    const variantId = btn.dataset.variantId;
    const card = btn.closest(".fr-addon-item");
    const state = btn.dataset.state || "off";
    const plusIcon = btn.querySelector(".fr-addon-item__icon-plus");
    const checkIcon = btn.querySelector(".fr-addon-item__icon-check");
    if (state === "off") {
      btn.disabled = true;
      const success = await addToCart(variantId, 1);
      if (success) {
        btn.dataset.state = "on";
        card?.classList.add("is-added");
        if (plusIcon) plusIcon.style.display = "none";
        if (checkIcon) checkIcon.style.display = "";
      }
      btn.disabled = false;
    } else {
      const item = cartState?.items.find(
        (i) => String(i.variant_id) === String(variantId),
      );
      if (item) {
        btn.disabled = true;
        await changeQty(item.key, 0);
        btn.dataset.state = "off";
        card?.classList.remove("is-added");
        if (plusIcon) plusIcon.style.display = "";
        if (checkIcon) checkIcon.style.display = "none";
        btn.disabled = false;
      }
    }
  });

  if (discountToggle) {
    discountToggle.addEventListener("click", () => {
      const expanded = discountToggle.getAttribute("aria-expanded") === "true";
      discountToggle.setAttribute("aria-expanded", String(!expanded));
      if (discountForm) discountForm.style.display = expanded ? "none" : "";
      if (!expanded && discountInput) discountInput.focus();
    });
  }

  if (discountApply) {
    discountApply.addEventListener("click", () => {
      const code = discountInput?.value.trim().toUpperCase();
      if (!code) return;
      sessionStorage.setItem("fr_discount_code", code);
      const checkoutLink = document.getElementById("fr-cart-checkout");
      if (checkoutLink)
        checkoutLink.href = `/checkout?discount=${encodeURIComponent(code)}`;
      showDiscountMsg(
        "Code applied! Discount will be shown at checkout.",
        "success",
      );
    });
  }

  if (discountInput) {
    discountInput.addEventListener("keydown", (e) => {
      if (e.key === "Enter") discountApply?.click();
    });
  }

  function restoreDiscount() {
    const saved = sessionStorage.getItem("fr_discount_code");
    if (saved && discountInput) {
      discountInput.value = saved;
      const checkoutLink = document.getElementById("fr-cart-checkout");
      if (checkoutLink)
        checkoutLink.href = `/checkout?discount=${encodeURIComponent(saved)}`;
    }
  }

  function showDiscountMsg(text, type) {
    if (!discountMsg) return;
    discountMsg.textContent = text;
    discountMsg.className = `fr-cart__discount-msg fr-cart__discount-msg--${type}`;
    discountMsg.style.display = "";
    clearTimeout(discountMsg._timer);
    discountMsg._timer = setTimeout(() => {
      discountMsg.style.display = "none";
    }, 4000);
  }

  document.addEventListener("fr:cart:open", openCart);
  document.addEventListener("fr:cart:close", closeCart);
  document.addEventListener("fr:cart:refresh", fetchCart);
  document.addEventListener("fr:cart:hydrate", (event) => {
    hydrateCart(
      event.detail && event.detail.cart,
      !!(event.detail && event.detail.open),
    );
  });

  window.FRCartDrawer = window.FRCartDrawer || {};
  window.FRCartDrawer.open = openCart;
  window.FRCartDrawer.refresh = fetchCart;
  window.FRCartDrawer.hydrate = hydrateCart;

  function disableImpulseCart() {
    if (window.theme?.cartDrawer) {
      window.theme.cartDrawer.open = () => openCart();
      window.theme.cartDrawer.close = () => closeCart();
    }
    const impulseDrawer = document.querySelector(
      "#CartDrawer, [data-ajax-cart-drawer], .js-cart-drawer",
    );
    if (impulseDrawer && impulseDrawer.id !== "fr-cart-drawer") {
      impulseDrawer.style.display = "none";
    }
  }

  function escHtml(str) {
    return String(str)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  function dispatch(eventName, detail = {}) {
    document.dispatchEvent(
      new CustomEvent(eventName, { detail, bubbles: true }),
    );
  }

  function init() {
    document.querySelectorAll('[type="submit"][name="add"]').forEach((btn) => {
      btn.dataset.originalText = btn.textContent.trim();
    });
    restoreDiscount();
    placeBundleOfferAboveCheckout();
    fetchCart();
    disableImpulseCart();
    document.addEventListener("shopify:section:load", disableImpulseCart);

    // The bundle offer is optional: expand the picker only when requested.
    if (shippingBar) {
      shippingBar.addEventListener("click", toggleBundlePicker);
      shippingBar.addEventListener("keydown", (event) => {
        if (event.key !== "Enter" && event.key !== " ") return;
        event.preventDefault();
        toggleBundlePicker();
      });
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();







